// src/oura/components/site/SiteIssuesCard.tsx
//
// What the crawl found, as two tabs: the issues, worst first, each opening
// its How to fix drawer; and the pages that matter, each opening the Page
// workshop. Top 5 of each, the rest behind "Show N more", and a footer line
// pinned to the card's foot, so a short list never ends in bare white.
//
// Above the tabs, what the live Overview's Site Health card said: the
// backend's verdict, the pages crawled with the errors and warnings, and what
// Site Health asks. The issues' counts carry their severity colour, as that
// card's "Holding it down" did; its top 3 are this table's first 3 rows.

import { useState, type ReactNode } from "react";
import { SearchCheckIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { pagePath } from "../../lib/oura";
import { ArrowLink, Dot, FOCUS_RING, FoldCard, Seg, ShowMore, TYPE, Tag, rowClick } from "@oura/ui";
import type { IssueSeverity } from "../../audit";
import { SITE_HEALTH_LINE, bandTone } from "./siteCopy";
import { SEVERITY_TONE, SEVERITY_WORD, pagesWith, progressText, type ScopedIssue } from "./siteIssues";
import type { SitePageRow } from "./sitePages";

const TOP = 5;

type Align = "left" | "center" | "right";
const ALIGN: Record<Align, string> = { left: "text-left", center: "text-center", right: "text-right" };
// The first and last cells carry the card's 20px gutter, so a table in a
// flush card lines up with the card's head.
const GUTTER = "px-2 first:pl-5 last:pr-5";
const th = (align: Align) => cn(TYPE.tc, "whitespace-nowrap py-2.5 font-medium text-gray-500", GUTTER, ALIGN[align]);
const td = (align: Align, extra?: string) => cn("h-12 border-t border-gray-100 py-1.5 align-middle", GUTTER, ALIGN[align], extra);
const EMPTY = "m-0 border-t border-gray-100 px-5 py-6 text-gray-500";
/** The line on the card's foot: `mt-auto` pins it there in the card's column. */
const FOOTER = cn(TYPE.tc, "m-0 mt-auto border-t border-gray-100 px-5 py-3 text-gray-400");
const LINK_BUTTON = cn(TYPE.tc, "whitespace-nowrap rounded font-medium text-blue-600 hover:underline", FOCUS_RING);
/** An issue's page count in its severity's colour. */
const COUNT_TEXT: Record<IssueSeverity, string> = { error: "text-rose-700", warning: "text-amber-700", notice: "text-gray-500" };

/** The first rows, then the rest in a second table with the same columns
 *  behind a native disclosure, so both tables line up column for column. */
function Split<T>({ rows, render, cols, head }: { rows: T[]; render: (rows: T[]) => ReactNode; cols: ReactNode; head: ReactNode }) {
    return (
        <>
            <table className="w-full table-fixed border-collapse">
                {cols}
                <thead>{head}</thead>
                <tbody>{render(rows.slice(0, TOP))}</tbody>
            </table>
            {rows.length > TOP && (
                <div className="border-t border-gray-100 px-5 pb-1">
                    <ShowMore count={rows.length - TOP}>
                        <table className="-mx-5 w-[calc(100%+2.5rem)] table-fixed border-collapse">
                            {cols}
                            <tbody>{render(rows.slice(TOP))}</tbody>
                        </table>
                    </ShowMore>
                </div>
            )}
        </>
    );
}

function IssuesTable({ issues, onFix }: { issues: ScopedIssue[]; onFix: (key: string) => void }) {
    const cols = (
        <colgroup>
            <col />
            <col className="w-16 sm:w-[104px]" />
            <col className="w-[88px] sm:w-[112px]" />
        </colgroup>
    );
    return (
        <Split
            rows={issues}
            cols={cols}
            head={
                <tr>
                    <th className={th("left")}>Issue</th>
                    <th className={th("center")}>Pages</th>
                    <th className={th("center")}><span className="sr-only">Action</span></th>
                </tr>
            }
            render={(rows) =>
                rows.map((issue) => {
                    const { count, progress } = progressText(issue);
                    return (
                        <tr key={issue.key} onClick={rowClick(() => onFix(issue.key))} className="cursor-pointer transition-colors hover:bg-gray-50">
                            <td className={td("left")}>
                                <span className={cn(TYPE.tb, "flex min-w-0 items-center gap-2 text-gray-900")} title={issue.desc}>
                                    <Dot tone={SEVERITY_TONE[issue.severity]} />
                                    <span className="sr-only">{SEVERITY_WORD[issue.severity]}: </span>
                                    <span className="min-w-0">{issue.label}</span>
                                </span>
                            </td>
                            <td className={td("center")}>
                                <span className={cn(TYPE.tb, "block font-medium tabular-nums", COUNT_TEXT[issue.severity])}>{count}</span>
                                {progress && <span className={cn(TYPE.tm, "block text-gray-500")}>{progress}</span>}
                                {/* Stated, not hidden: the count counts only the
                                    pages still in scope, and without this it
                                    would silently disagree with the backend. */}
                                {issue.setAside > 0 && (
                                    <span className={cn(TYPE.tm, "block text-gray-400")}>{issue.setAside} out of scope</span>
                                )}
                            </td>
                            <td className={td("center")}>
                                <button
                                    type="button"
                                    className={LINK_BUTTON}
                                    aria-label={`How to fix: ${issue.label}`}
                                    onClick={() => onFix(issue.key)}
                                >
                                    How to fix
                                </button>
                            </td>
                        </tr>
                    );
                })
            }
        />
    );
}

function PagesTable({
    pages,
    hasClicks,
    onOpenPage,
}: {
    pages: SitePageRow[];
    hasClicks: boolean;
    onOpenPage?: (page: { url: string; title: string }) => void;
}) {
    const cols = (
        <colgroup>
            <col />
            {hasClicks && <col className="w-[72px]" />}
            <col className="w-[64px]" />
            {onOpenPage && <col className="w-[136px]" />}
        </colgroup>
    );
    return (
        // Four columns do not fit a phone; the table scrolls inside the card
        // rather than squeezing the page name to nothing. `relative` keeps the
        // absolutely placed screen-reader labels inside the scroller, or they
        // would widen the page itself.
        <div className="relative overflow-x-auto">
            <div className="min-w-[460px]">
                <Split
                    rows={pages}
                    cols={cols}
                    head={
                        <tr>
                            <th className={th("left")}>Page</th>
                            {/* Centred, like the Issues tab's own count column:
                                the two tabs of one card disagreed, and a
                                one-digit number under the end of a header word
                                reads as though it belongs to the next column
                                (#4025 FE-18). */}
                            {hasClicks && <th className={th("center")}>Clicks</th>}
                            <th className={th("center")}>Issues</th>
                            {onOpenPage && <th className={th("center")}><span className="sr-only">Action</span></th>}
                        </tr>
                    }
                    render={(rows) =>
                        rows.map((p) => (
                            <tr
                                key={p.url}
                                onClick={onOpenPage ? rowClick(() => onOpenPage({ url: p.url, title: p.title })) : undefined}
                                className={onOpenPage ? "cursor-pointer transition-colors hover:bg-gray-50" : undefined}
                            >
                                <td className={td("left")}>
                                    <span className={cn(TYPE.tb, "block truncate font-medium text-gray-900")} title={p.url}>
                                        {p.title} <span className={cn(TYPE.tc, "font-normal text-gray-400")}>{pagePath(p.url)}</span>
                                    </span>
                                    <span className={cn(TYPE.tm, "block truncate text-gray-500")}>{p.line}</span>
                                </td>
                                {hasClicks && <td className={td("center", cn(TYPE.tb, "tabular-nums text-gray-900"))}>{p.clicks}</td>}
                                <td className={td("center", cn(TYPE.tb, "tabular-nums text-gray-900"))}>{p.issues ?? "–"}</td>
                                {onOpenPage && (
                                    <td className={td("center")}>
                                        <button type="button" className={LINK_BUTTON} onClick={() => onOpenPage({ url: p.url, title: p.title })}>
                                            View page details
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))
                    }
                />
            </div>
        </div>
    );
}

export function SiteIssuesCard({
    issues,
    checksRun,
    onFix,
    pages,
    hasClicks,
    clickDays,
    pagesCrawled,
    setAsideCount,
    crawlDay,
    unreadText,
    health,
    busy,
    refresh,
    progress,
    onOpenPage,
    onOpenAllPages,
    onConnectSearch,
}: {
    /** The findings worth listing, worst first, out-of-scope pages removed. */
    issues: ScopedIssue[];
    /** Every check the crawl ran, including the ones that found nothing. */
    checksRun: number;
    onFix: (key: string) => void;
    pages: SitePageRow[];
    /** Search Console backs the clicks; without it the column is absent. */
    hasClicks: boolean;
    clickDays: number;
    pagesCrawled: number;
    setAsideCount: number;
    crawlDay: string | null;
    /** Set when the crawl could not read the site: said instead of the lists. */
    unreadText: string | null;
    /** Site Health and the backend's verdict on it, when the crawl scored the site. */
    health?: { score: number; verdict: string | null } | null;
    /** A crawl refresh is running: dimmed, never blanked. */
    busy: boolean;
    refresh?: ReactNode;
    progress?: ReactNode;
    onOpenPage?: (page: { url: string; title: string }) => void;
    onOpenAllPages?: () => void;
    onConnectSearch?: () => void;
}) {
    const [tab, setTab] = useState<"issues" | "pages">("issues");
    const more = Math.max(0, pagesCrawled - pages.length - setAsideCount);
    // Errors plus warnings, counted per affected page: the Issues tile's
    // number, so the tile and the tab never state two figures for one thing.
    const flagged = pagesWith(issues, "error") + pagesWith(issues, "warning");
    const passed = checksRun - issues.length;
    const meta = [crawlDay && `${crawlDay} crawl`, hasClicks && `clicks over the last ${clickDays} days`].filter(Boolean).join(" · ");
    const errors = pagesWith(issues, "error");
    const warnings = pagesWith(issues, "warning");

    return (
        <FoldCard
            id="crawl"
            anchor="issues"
            className="scroll-mt-6"
            title="What the crawl found"
            icon={{ icon: SearchCheckIcon, area: "site" }}
            flush
            foldedFact={`${flagged} ${flagged === 1 ? "issue" : "issues"}`}
            right={
                <Seg
                    as="tablist"
                    ariaLabel="Issues or pages"
                    items={[
                        { key: "issues", label: "Issues", count: flagged },
                        { key: "pages", label: "Pages", count: pagesCrawled > 0 ? pagesCrawled : undefined },
                    ]}
                    value={tab}
                    onChange={setTab}
                />
            }
        >
            {/* A column that fills the card, so the footer sits on the card's
                foot whatever the height of the card beside it. */}
            <div className="flex h-full flex-col">
                {!unreadText && (
                    <div className="px-5 pb-3">
                        {/* Score, then the verdict chip, then the counts --
                            the same order as "What a machine can read" beside
                            it. The two cards read the same facts in two
                            different orders, with this one leading on a chip
                            and never showing its score at all (#4025 FE-17).
                            A site with no score shows none: it is a
                            measurement, not a zero. */}
                        <p className={cn(TYPE.tc, "m-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-500")}>
                            {typeof health?.score === "number" && (
                                <span className="tabular-nums">Site health {health.score} / 100</span>
                            )}
                            {/* Wraps on a phone: the backend's sentence is wider than the card there. */}
                            {health?.verdict && <Tag tone={bandTone(health.score)} className="!whitespace-normal">{health.verdict}</Tag>}
                            <span className="tabular-nums">
                                {[
                                    pagesCrawled > 0 && `${pagesCrawled} ${pagesCrawled === 1 ? "page" : "pages"} crawled`,
                                    `${errors} ${errors === 1 ? "error" : "errors"}`,
                                    `${warnings} ${warnings === 1 ? "warning" : "warnings"}`,
                                ].filter(Boolean).join(" · ")}
                            </span>
                        </p>
                        <p className={cn(TYPE.tm, "m-0 mt-1 text-gray-400")}>{SITE_HEALTH_LINE}</p>
                    </div>
                )}
                {(meta || refresh) && (
                    <div className={cn(TYPE.tc, "flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-5 pb-3 text-gray-500")}>
                        <span>{meta}</span>
                        {refresh}
                    </div>
                )}
                {progress && <div className="space-y-1 px-5 pb-3 empty:hidden">{progress}</div>}
                <div
                    role="tabpanel"
                    aria-label={tab === "issues" ? "Issues" : "Pages"}
                    aria-busy={busy}
                    className={cn("flex flex-1 flex-col transition-opacity", busy && "opacity-50")}
                >
                    {/* Said first, and the lists still render under it: a crawl
                        that read no page can still carry the finding that
                        explains why (every page a redirect loop). */}
                    {unreadText && <p className={cn(TYPE.tc, EMPTY, "text-gray-600")}>{unreadText}</p>}
                    {tab === "issues" ? (
                        issues.length > 0 ? (
                            <IssuesTable issues={issues} onFix={onFix} />
                        ) : (
                            !unreadText && (
                                <p className={cn(TYPE.tc, EMPTY)}>The crawl read the site and flagged nothing on this scan.</p>
                            )
                        )
                    ) : pages.length > 0 ? (
                        <PagesTable pages={pages} hasClicks={hasClicks} onOpenPage={onOpenPage} />
                    ) : (
                        !unreadText && <p className={cn(TYPE.tc, EMPTY)}>No priority pages found on this scan.</p>
                    )}
                    {tab === "issues" && !unreadText && checksRun > 0 && (
                        <p className={FOOTER}>
                            {issues.length} of {checksRun} {checksRun === 1 ? "check" : "checks"} found something.
                            {passed > 0 && ` The other ${passed} passed.`}
                        </p>
                    )}
                    {tab === "pages" && !unreadText && (
                        <div className={cn(FOOTER, "flex flex-wrap items-center justify-between gap-x-3 gap-y-1")}>
                            <span>
                                {[
                                    more > 0 && `${more} more ${more === 1 ? "page" : "pages"}`,
                                    setAsideCount > 0 && `${setAsideCount} out of scope`,
                                ].filter(Boolean).join(" · ")}
                                {!hasClicks && onConnectSearch && (
                                    <>
                                        {more > 0 || setAsideCount > 0 ? " · " : ""}
                                        Ranked by internal links.{" "}
                                        <button type="button" className={LINK_BUTTON} onClick={onConnectSearch}>
                                            Connect Search Console
                                        </button>
                                    </>
                                )}
                            </span>
                            {onOpenAllPages && <ArrowLink onClick={onOpenAllPages}>Open all pages</ArrowLink>}
                        </div>
                    )}
                </div>
            </div>
        </FoldCard>
    );
}
